import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { ChangeDetectionStrategy, Component, computed, inject, input, resource } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { RouterModule } from "@angular/router";

import { WirelessHealthStatus } from "../../../apis/networkdashboard/rest/models";
import { HealthService } from "../../../apis/networkdashboard/rest/services";
import { WirelessTicket } from "../../../components/models/wireless-ticket";
import { phraseDetailPath } from "../../../pages/subscription-detail/subscription-detail-redirection";
import { WirelessListItem } from "../../../pages/subscriptions/wireless-subscriptions-results";
import { AliasEditorComponent } from "../../common/alias-editor/alias-editor.component";
import { LoaderComponent } from "../../common/loader/loader";
import { openDialogIncident } from "../../dialogs/dialog-incident/dialog-incident";
import { DialogWirelessTicketsComponent } from "../../dialogs/dialog-wireless-tickets/dialog-wireless-tickets";
import { EMPTY_WIRELESS_HEALTH } from "../../models/HealthIndicator";
import { KpiRowComponent } from "../kpi-row/kpi-row";
import { ServiceSubtitleComponent } from "../service-subtitle/service-subtitle";
import { ServiceTags } from "../service-tags/service-tags";

@Component({
  selector: "wireless-element",
  templateUrl: "wireless-element.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AliasEditorComponent,
    AngularSvgIconModule,
    KpiRowComponent,
    LoaderComponent,
    RouterModule,
    ServiceTags,
    ServiceSubtitleComponent,
    TranslatePipe,
  ],
})
export class WirelessElement {
  private dialog = inject(MatDialog);
  private healthService = inject(HealthService);

  protected subscription = input.required<WirelessListItem>();
  protected orgTickets = input<WirelessTicket[]>([]);
  private tickets = computed(() => {
    const jiraLocationId = this.subscription().jiraLocationId;
    return jiraLocationId ? this.orgTickets().filter((ticket) => ticket.locations.includes(jiraLocationId)) : [];
  });
  protected openTicketCount = computed(() => this.tickets().length);
  private lastTicket = computed(() => this.tickets().at(0));
  protected lastTicketId = computed(() => this.lastTicket()?.ticketId ?? "");
  protected lastTicketSummary = computed(() => this.lastTicket()?.summary ?? "");
  protected hasOpenTickets = computed(() => this.openTicketCount() > 0);
  protected moreTicketCount = computed(() => this.openTicketCount() - 1);
  protected hasMoreTickets = computed(() => this.moreTicketCount() > 0);
  protected productType = computed(() => this.subscription().product.type);
  protected productTag = computed(() => this.subscription().product.tag);

  protected sensorPresent = computed(() => this.subscription().sensorPresent);
  protected subscriptionId = computed(() => this.subscription().subscriptionId);
  protected customerDescription = computed(() => this.subscription().customerDescription);
  protected tags = computed(() => this.subscription().tags);
  protected vendor = computed(() => this.subscription().supplier);

  protected routerLink = computed(() => phraseDetailPath(this.productType(), this.productTag(), this.subscriptionId()));

  protected kpiResource = resource({
    params: () => ({ subscription_id: this.subscriptionId(), count_num_access_points: true }),
    loader: ({ params }) => this.healthService.getWirelessHealthStatus(params),
  });
  protected loading = computed(() => this.kpiResource.isLoading());
  /** Health status once loaded; undefined while loading or when the fetch failed. */
  private health = computed(() => (this.kpiResource.hasValue() ? this.kpiResource.value() : undefined));
  protected kpiData = computed<WirelessHealthStatus>(() => this.health() ?? EMPTY_WIRELESS_HEALTH);
  protected activeAps = computed(() => this.health()?.activeAps);
  protected inactiveAps = computed(() => this.health()?.inactiveAps);
  protected hasActiveAps = computed(() => this.activeAps() !== undefined);
  protected hasInactiveAps = computed(() => this.inactiveAps() !== undefined);

  openIncidentDialog(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    openDialogIncident(this.dialog, this.subscription());
  }

  openTicketsDialog(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.dialog
      .open(DialogWirelessTicketsComponent, {
        data: { tickets: this.tickets(), scope: "location" },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === "viewAll") {
          this.dialog.open(DialogWirelessTicketsComponent, {
            data: { tickets: this.orgTickets(), scope: "organisation" },
          });
        }
      });
  }
}
